// 🔧 store_settings.js
// ✅ Purpose: Manager-facing Store settings page
// ✅ Features:
// - Manages public Store description and banner image URL
// - Controls pickup and shipping availability
// - Stores pickup instructions
// - Supports flat-rate shipping
// - Supports free-shipping threshold
// - Saves settings to /events/{eventId}/settings/store
// - Shows success and error notifications for 20 seconds
// - Embeds event-level Stripe payment configuration at the bottom
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { db, storage } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import { hasPurchasedAddOn } from "../../utils/event_addons";
import AddonRequired from "../../components/AddonRequired";
import PaymentSettingsPanel from "../../components/commerce/payment_settings_panel";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";

const StoreSettings = () => {
  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  const [form, setForm] = useState({
    storeEnabled: true,
    storeDescription: "",
    storeBannerImageUrl: "",

    pickupEnabled: true,
    pickupInstructions: "",

    shippingEnabled: false,
    flatShippingDollars: "0.00",
    freeShippingThresholdDollars: "",

    orderEmailMessage: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [bannerUploadProgress, setBannerUploadProgress] = useState(0);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const storeEnabled = hasPurchasedAddOn(
    eventDoc,
    "store"
  );
  const ticketsEnabled = hasPurchasedAddOn(eventDoc, "tickets");
  const auctionEnabled = hasPurchasedAddOn(eventDoc, "auction");
  const sponsorsEnabled = hasPurchasedAddOn(eventDoc, "sponsors");
  const vendorsEnabled = hasPurchasedAddOn(eventDoc, "vendors");
  // Stripe Connect is shared payment infrastructure. It must be reachable
  // from every Power Up that accepts direct event-account payments.
  const commerceEnabled = storeEnabled || ticketsEnabled || auctionEnabled || sponsorsEnabled || vendorsEnabled;
  useEffect(() => {
    if (!status && !error) return;

    const timer = setTimeout(() => {
      setStatus(null);
      setError(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status, error]);

  const centsToDollars = (value) => {
    const numeric = Number(value || 0);
    return numeric > 0 ? (numeric / 100).toFixed(2) : "";
  };

  const dollarsToCents = (value) => {
    const numeric = Number(value || 0);
    return Math.round(numeric * 100);
  };

  useEffect(() => {
    const loadSettings = async () => {
      if (!eventId) return;

      setLoading(true);

      try {
        const settingsSnap = await getDoc(
          doc(db, "events", eventId, "settings", "store")
        );

        const data = settingsSnap.exists() ? settingsSnap.data() : {};

        setForm({
          storeEnabled: data.storeEnabled !== false,
          storeDescription: data.storeDescription || "",
          storeBannerImageUrl: data.storeBannerImageUrl || "",

          pickupEnabled: data.pickupEnabled !== false,
          pickupInstructions: data.pickupInstructions || "",

          shippingEnabled: data.shippingEnabled === true,
          flatShippingDollars: centsToDollars(data.flatShippingCents) || "0.00",
          freeShippingThresholdDollars:
            centsToDollars(data.freeShippingThresholdCents) || "",

          orderEmailMessage: data.orderEmailMessage || "",
        });

        logger.log("✅ Store settings loaded", {
          eventId,
        });
      } catch (err) {
        logger.error("❌ Failed loading Store settings:", {
          value: err,
        });

        setError("Unable to load Store settings.");
      } finally {
        setLoading(false);
      }
    };

    if (ready && eventId) {
      loadSettings();
    }
  }, [ready, eventId]);

  const handleBannerImageUpload = async (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const maxSizeBytes = 5 * 1024 * 1024;

    if (file.size > maxSizeBytes) {
      setError("Store banner image must be 5 MB or smaller.");
      e.target.value = "";
      return;
    }

    try {
      setError(null);
      setStatus(null);
      setUploadingBanner(true);
      setBannerUploadProgress(0);

      const cleanFileName = file.name
        .toLowerCase()
        .replace(/[^a-z0-9.]+/g, "-")
        .replace(/^-+|-+$/g, "");

      const storagePath = `eventAssets/${eventId}/store/banner-${Date.now()}-${cleanFileName}`;
      const storageRef = ref(storage, storagePath);

      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );

          setBannerUploadProgress(progress);
        },
        (err) => {
          logger.error("❌ Store banner upload failed:", {
            value: err,
          });

          setError("Unable to upload Store banner image.");
          setUploadingBanner(false);
        },
        async () => {
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);

          setForm((prev) => ({
            ...prev,
            storeBannerImageUrl: downloadUrl,
          }));

          setStatus("✅ Store banner image uploaded. Save settings to keep this image.");
          setUploadingBanner(false);
          setBannerUploadProgress(100);
        }
      );
    } catch (err) {
      logger.error("❌ Store banner upload error:", {
        value: err,
      });

      setError("Unable to upload Store banner image.");
      setUploadingBanner(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    setStatus(null);
    setError(null);
    setSaving(true);

    try {
      await setDoc(
        doc(db, "events", eventId, "settings", "store"),
        {
          storeEnabled: form.storeEnabled === true,
          storeDescription: String(form.storeDescription || "").trim(),
          storeBannerImageUrl: String(form.storeBannerImageUrl || "").trim(),

          pickupEnabled: form.pickupEnabled === true,
          pickupInstructions: String(form.pickupInstructions || "").trim(),

          shippingEnabled: form.shippingEnabled === true,
          flatShippingCents: dollarsToCents(form.flatShippingDollars),
          freeShippingThresholdCents: form.freeShippingThresholdDollars
            ? dollarsToCents(form.freeShippingThresholdDollars)
            : 0,

          orderEmailMessage: String(form.orderEmailMessage || "").trim(),

          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      logger.log("✅ Store settings saved", {
        eventId,
      });

      setStatus("✅ Store settings saved successfully.");
      setBannerUploadProgress(0);
      setUploadingBanner(false);
    } catch (err) {
      logger.error("❌ Failed saving Store settings:", {
        value: err,
      });

      setError("Unable to save Store settings.");
    } finally {
      setSaving(false);
    }
  };

  if (
    ready &&
    eventDoc &&
    !commerceEnabled
  ) {
    return (
      <AddonRequired
        eventslug={eventslug}
        title="Commerce Power Up Required"
        description="Enable Auctions, Event Merch Store, Tickets, Sponsors, or Vendors before configuring payment settings."
      />
    );
  }

  if (!ready || !eventId || eventLoading || loading) {
    return <FullPageSpinner message="Loading Store Settings..." />;
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1100px" }}>
        {status && <div className="alert alert-success">{status}</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        <div className="row mb-4">
          <div className="col text-start">
            <h1 className="display-5">🛒 Store Settings</h1>

            <p className="lead text-muted mb-0">
              Manage Store and payment settings for {eventDoc?.name || "this event"}.
            </p>
          </div>
        </div>

        {storeEnabled && <form onSubmit={handleSave}>
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white">
              <strong>Public Store Content</strong>
            </div>

            <div className="card-body">
              <div className="form-check border rounded p-3 mb-3">
                <input
                  type="checkbox"
                  name="storeEnabled"
                  id="storeEnabled"
                  className="form-check-input"
                  checked={form.storeEnabled}
                  onChange={handleChange}
                />

                <label className="form-check-label fw-bold" htmlFor="storeEnabled">
                  Store Enabled
                </label>

                <div className="form-text">
                  Allows the public Store page to display products.
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Store Description</label>

                <textarea
                  name="storeDescription"
                  className="form-control"
                  rows={4}
                  value={form.storeDescription}
                  onChange={handleChange}
                  placeholder="Tell visitors what they can buy, how pickup works, or how this Store supports the event or club."
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Store Banner Image URL</label>

                <input
                  type="url"
                  name="storeBannerImageUrl"
                  className="form-control"
                  value={form.storeBannerImageUrl}
                  onChange={handleChange}
                  placeholder="https://..."
                />

                <div className="form-text">
                  Paste a hosted image URL or upload a Store banner image below.
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">
                  Upload Store Banner Image
                </label>

                <input
                  type="file"
                  accept="image/*"
                  className="form-control"
                  onChange={handleBannerImageUpload}
                  disabled={uploadingBanner}
                />

                <div className="form-text">
                  Maximum file size: 5 MB. Recommended: wide banner image.
                </div>

                {uploadingBanner && (
                  <div className="progress mt-3" style={{ height: "24px" }}>
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{ width: `${bannerUploadProgress}%` }}
                      aria-valuenow={bannerUploadProgress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      {bannerUploadProgress}%
                    </div>
                  </div>
                )}

                {bannerUploadProgress > 0 &&
                  bannerUploadProgress < 100 &&
                  uploadingBanner && (
                    <div className="progress mt-3" style={{ height: "24px" }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        role="progressbar"
                        style={{ width: `${bannerUploadProgress}%` }}
                      >
                        {bannerUploadProgress}%
                      </div>
                    </div>
                )}

                {bannerUploadProgress === 100 && !saving && (
                  <div className="alert alert-success mt-3 mb-0">
                    ✅ Banner uploaded. Remember to save Store Settings.
                  </div>
                )}

                {form.storeBannerImageUrl && (
                  <div className="mt-3">
                    <div className="small text-muted mb-2">Current Banner Preview</div>

                    <img
                      src={form.storeBannerImageUrl}
                      alt="Store banner preview"
                      className="img-fluid rounded border"
                      style={{
                        width: "100%",
                        height: "auto",
                        border: "1px solid #ddd",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "0.375rem",
                      }}
                    />
                  </div>
                )}
              </div>

            </div>
          </div>

          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white">
              <strong>Pickup</strong>
            </div>

            <div className="card-body">
              <div className="form-check border rounded p-3 mb-3">
                <input
                  type="checkbox"
                  name="pickupEnabled"
                  id="pickupEnabled"
                  className="form-check-input"
                  checked={form.pickupEnabled}
                  onChange={handleChange}
                />

                <label className="form-check-label fw-bold" htmlFor="pickupEnabled">
                  Pickup Enabled
                </label>

                <div className="form-text">
                  Allows customers to select pickup at the event.
                </div>
              </div>

              <label className="form-label">Pickup Instructions</label>

              <textarea
                name="pickupInstructions"
                className="form-control"
                rows={4}
                value={form.pickupInstructions}
                onChange={handleChange}
                placeholder="Example: Pick up orders at the registration tent between 9 AM and 2 PM."
              />
            </div>
          </div>

          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white">
              <strong>Shipping</strong>
            </div>

            <div className="card-body">
              <div className="form-check border rounded p-3 mb-3">
                <input
                  type="checkbox"
                  name="shippingEnabled"
                  id="shippingEnabled"
                  className="form-check-input"
                  checked={form.shippingEnabled}
                  onChange={handleChange}
                />

                <label className="form-check-label fw-bold" htmlFor="shippingEnabled">
                  Shipping Enabled
                </label>

                <div className="form-text">
                  Allows customers to select shipping. USPS rating can come later.
                </div>
              </div>

              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label">Flat Shipping Rate</label>

                  <div className="input-group">
                    <span className="input-group-text">$</span>

                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      name="flatShippingDollars"
                      className="form-control"
                      value={form.flatShippingDollars}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">Free Shipping Threshold</label>

                  <div className="input-group">
                    <span className="input-group-text">$</span>

                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      name="freeShippingThresholdDollars"
                      className="form-control"
                      value={form.freeShippingThresholdDollars}
                      onChange={handleChange}
                      placeholder="Optional"
                    />
                  </div>

                  <div className="form-text">
                    Leave blank or 0 to disable free shipping.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white">
              <strong>Order Message</strong>
            </div>

            <div className="card-body">
              <label className="form-label">Message for Store Customers</label>

              <textarea
                name="orderEmailMessage"
                className="form-control"
                rows={4}
                value={form.orderEmailMessage}
                onChange={handleChange}
                placeholder="Optional message shown later on receipts or order confirmation emails."
              />
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : "💾 Save Store Settings"}
            </button>

            <Link to={`/${eventslug}/store`} className="btn btn-outline-dark">
              View Public Store
            </Link>

            <Link to={`/${eventslug}/store_products`} className="btn btn-outline-secondary">
              Manage Products
            </Link>
          </div>
        </form>}

        <PaymentSettingsPanel
          eventId={eventId}
          eventslug={eventslug}
          onNotice={setStatus}
          onError={setError}
        />
      </div>
    </section>
  );
};

export default StoreSettings;
